import { REDIRECT_BUFFER_AFTER_CELEBRATION_MS } from "./celebrationTiming";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { IntakeData } from "@/lib/types";
import { trackReferralCompleted } from "@/lib/referralTracker";
import {
  SubmitCelebrationCard,
} from "./SubmitCelebrationCard";

interface SuccessCardProps {
  data: IntakeData;
}

const JEMA_MANAGER_URL =
  import.meta.env.VITE_JEMA_MANAGER_URL?.trim() ||
  "https://app.jemaservices.com.co/";

/** Intake v2 final screen — same celebration thank-you used after review (not the long SuccessStep dashboard page). */
export const SuccessCard = memo(function SuccessCard({ data }: SuccessCardProps) {
  const { i18n } = useLingui();
  const [redirecting, setRedirecting] = useState(false);
  const redirectTimerRef = useRef<number | null>(null);

  const redirectToManager = useCallback(() => {
    window.location.assign(JEMA_MANAGER_URL);
  }, []);

  const handleCelebrationComplete = useCallback(() => {
    setRedirecting(true);
    redirectTimerRef.current = window.setTimeout(
      redirectToManager,
      REDIRECT_BUFFER_AFTER_CELEBRATION_MS,
    );
  }, [redirectToManager]);

  useEffect(() => {
    void trackReferralCompleted(data.firstName?.trim() || "");
  }, [data.firstName]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current != null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-lg sm:max-w-2xl">
      <SubmitCelebrationCard
        data={{ firstName: data.firstName }}
        showVerifyHint={false}
        showInboxButton={false}
        className="px-5 py-10 sm:px-10 sm:py-14"
        onComplete={handleCelebrationComplete}
        descriptionContent={
          <span className="block text-pretty text-base leading-relaxed text-gray-600 sm:text-lg">
            {i18n._(
              msg`Your information has been received and your email is verified. Our medical team will review your intake shortly.`,
            )}
          </span>
        }
      />
      {redirecting ? (
        <p
          className="submit-celebration-fade-up mt-4 text-center text-sm font-medium text-[#166B93]/80"
          aria-live="polite"
        >
          {i18n._(msg`Redirecting to Jema Manager…`)}
        </p>
      ) : (
        <p className="sr-only" aria-live="polite">
          {i18n._(
            msg`You will be redirected to Jema Manager in a few seconds.`,
          )}
        </p>
      )}
    </div>
  );
});
